/**
 * Workout Session API Service
 *
 * API client for workout session management
 */

import api from '@/lib/api';
import {
  StartSessionRequest,
  StartSessionResponse,
  SessionProgressResponse,
  CompleteSetRequest,
  CompleteSessionResponse,
  UpdateSetMetadataRequest,
  UpdateSetMetadataResponse,
  WorkoutSessionListDTO,
  WorkoutSessionDetailDTO,
} from '@/types/workoutSession';

const API_BASE_URL = import.meta.env.VITE_API_URL;

class WorkoutSessionApi {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/api/v1/workout-sessions`;
  }

  /**
   * Get paginated list of workout sessions for the current user
   */
  async getSessions(
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<WorkoutSessionListDTO> {
    const params: any = { page, limit };
    if (status) {
      params.status = status;
    }
    const response = await api.get<WorkoutSessionListDTO>(`${this.baseURL}/`, { params });
    return response.data;
  }

  /**
   * Get detailed information for a specific workout session
   */
  async getSessionDetail(sessionId: string): Promise<WorkoutSessionDetailDTO> {
    const response = await api.get<WorkoutSessionDetailDTO>(`${this.baseURL}/${sessionId}`);
    return response.data;
  }

  /**
   * Start a new workout session
   */
  async startSession(workoutId: string): Promise<StartSessionResponse> {
    const request: StartSessionRequest = { workout_id: workoutId };
    const response = await api.post<StartSessionResponse>(`${this.baseURL}/start`, request);
    return response.data;
  }

  /**
   * Get current progress for a session
   */
  async getProgress(sessionId: string): Promise<SessionProgressResponse> {
    const response = await api.get<SessionProgressResponse>(
      `${this.baseURL}/${sessionId}/progress`
    );
    return response.data;
  }

  /**
   * Mark a set as started
   */
  async startSet(sessionId: string, setId: string): Promise<void> {
    const request: CompleteSetRequest = { set_id: setId };
    await api.post(`${this.baseURL}/${sessionId}/start-set`, request);
  }

  /**
   * Mark a set as completed
   */
  async completeSet(sessionId: string, setId: string): Promise<void> {
    const request: CompleteSetRequest = { set_id: setId };
    await api.post(`${this.baseURL}/${sessionId}/complete-set`, request);
  }

  /**
   * Pause an active session
   */
  async pauseSession(sessionId: string): Promise<void> {
    await api.post(`${this.baseURL}/${sessionId}/pause`);
  }

  /**
   * Resume a paused session
   */
  async resumeSession(sessionId: string): Promise<void> {
    await api.post(`${this.baseURL}/${sessionId}/resume`);
  }

  /**
   * Complete a workout session
   */
  async completeSession(sessionId: string): Promise<CompleteSessionResponse> {
    const response = await api.post<CompleteSessionResponse>(
      `${this.baseURL}/${sessionId}/complete`
    );
    return response.data;
  }

  /**
   * Update metadata for a specific set during an active workout session
   */
  async updateSetMetadata(
    sessionId: string,
    setId: string,
    metadata: UpdateSetMetadataRequest
  ): Promise<UpdateSetMetadataResponse> {
    const response = await api.patch<UpdateSetMetadataResponse>(
      `${this.baseURL}/${sessionId}/sets/${setId}/metadata`,
      metadata
    );
    return response.data;
  }

  /**
   * Get or create a draft workout session for a workout
   * Checks for existing active session, or creates a new one if none exists
   */
  async getOrCreateDraftSession(workoutId: string): Promise<StartSessionResponse> {
    // Get active sessions for the user
    const sessions = await this.getSessions(1, 100, 'in_progress');
    
    // Find existing active session for this workout
    const existingSession = sessions.sessions.find(
      (session) => session.workout_id === workoutId
    );
    
    if (existingSession) {
      // Return existing session details
      return {
        session_id: existingSession.session_id,
        workout_id: existingSession.workout_id,
        status: existingSession.status,
        total_sets: existingSession.total_sets,
      };
    }
    
    // No existing session, create a new one
    return await this.startSession(workoutId);
  }
}

// Export singleton instance
export const workoutSessionApi = new WorkoutSessionApi();

